import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientAppointmentService } from './../../../../core/services/patient-appointment.service';
import { PatientCommunicationService } from './../../../../core/services/patient-communication.service';
import { PatientService } from './../../../../core/services/patient.service';
import { AppointmentTypeService } from './../../../../core/services/appointment-type.service';
import { confirmationTooltip } from 'src/app/core/utils/appointment-confirmation';
import { inject } from '@angular/core';
import { GroupService } from 'src/app/core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

@Component({
  selector: 'app-patient-appointment-list',
  templateUrl: './patient-appointment-list.component.html',
  styleUrls: ['./patient-appointment-list.component.scss']
})
export class PatientAppointmentListComponent extends BaseComponent implements OnInit {
  private readonly groupService = inject(GroupService);

  /** A state unit (tasks/state-mode.md): no reminder emails. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  readonly confirmationTooltip = confirmationTooltip;

  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();

  patient?: any;
  appointments!: any[] | null;
  emails?: any[] | null = [];
  /** The practice's appointment types, for whether each booking's type sends reminders. */
  appointmentTypes: any[] = [];

  constructor(
    private router: Router,
    private patientAppointmentService: PatientAppointmentService,
    private patientCommunication: PatientCommunicationService,
    private patientService: PatientService,
    private appointmentTypeService: AppointmentTypeService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$)
      ).subscribe(currentPatient => this.patient = currentPatient);

    this.appointmentTypeService.appointmentTypes$
      .pipe(takeUntil(this.destroy$))
      .subscribe((types: any[] | null) => this.appointmentTypes = types ?? []);

    this.patientAppointmentService.patientAppointments$
      .pipe(takeUntil(this.destroy$))
      .subscribe((appointments: any[] | null) => {
        this.appointments = this.groupAndSortAppointments(appointments);
      });

    this.patientCommunication.patientEmails$
      .pipe(takeUntil(this.destroy$))
      .subscribe((emails: any[] | null) => {
        this.emails = emails;
      });
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  private groupAndSortAppointments(appointments: any[] | null): any[] | null {
    if (!appointments || !appointments.length) {
      return appointments;
    }

    // Group appointments by formatted date
    const grouped = _.groupBy(appointments, appointment => moment(appointment.date).format('YYYY-MM-DD'));

    // Sort the groups by date in descending order and transform into an array
    return _.chain(grouped)
      .map((value, key) => ({ date: key, appointments: value }))
      .orderBy(group => group.date, 'desc')
      .value();
  }

  /**
   * The reminder line under an appointment. The scheduler sends reminders at about 06:00 on the day
   * of the appointment, where the booking (or else its type) says to.
   */
  getReminderMessage(appointment: any): string {
    const momentDate = moment.utc(appointment?.date);
    const sent = (this.emails ?? []).find(mail =>
      mail.status === 'sent' &&
      moment.utc(mail.createdAt).isSame(momentDate, 'day') &&
      String(mail.subject || '').toLowerCase().includes('reminder')
    );
    if (sent) return `A reminder was sent on ${moment.utc(sent.createdAt).format('DD/MM/YYYY')}`;
    if (momentDate.isBefore(moment.utc(), 'day')) return '';

    const type = (this.appointmentTypes ?? []).find(t => t.id === appointment?.appointmentTypeId);
    const sends = type?.suppressSending !== true && (appointment?.sendReminder ?? type?.sendReminders ?? true);
    if (!sends) return 'No reminder will be sent for this appointment';
    if (momentDate.isSame(moment.utc(), 'day') && moment().hour() >= 6) {
      return "No reminder will be sent: today's 6 AM reminders have already gone out";
    }
    return `A reminder will be sent at 6 AM on the day of the appointment (${momentDate.format('DD/MM/YYYY')})`;
  }

  canSendReminder(date: string) {
    return !this.stateMode && this.patient?.email && moment.utc(date).isAfter(moment.utc(), 'day');
  }

  sendReminderEmail(event: MouseEvent, appointment: any) {
    event.preventDefault();
    event.stopPropagation();

    const sub = this.patientAppointmentService.sendAppointmentReminder(this.patient, appointment)
      .subscribe({
        next: (result: any) => {
          this.feedbackService.showToast('Appointment reminder sent successfully');
          sub.unsubscribe();
        },
        error: (error) => {
          console.error(error);
          this.feedbackService.showToast('There was an error sending the appointment reminder. Please try again.');
        }
      })

  }


  loadAppointment(appointment: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    //this.patientAppointmentService.setEditingAppointment(appointment);
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['edit-appointment', appointment.id],
      }
    }]);
  }
}
